import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const LEAD_STAGES = ['new', 'contacted', 'qualified', 'site_visit', 'proposal_sent', 'negotiation', 'won', 'lost'];
const STAGE_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  site_visit: 'Site Visit',
  proposal_sent: 'Proposal Sent',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
};
const STAGE_COLORS = {
  new: 'bg-gray-400',
  contacted: 'bg-blue-400',
  qualified: 'bg-indigo-400',
  site_visit: 'bg-purple-400',
  proposal_sent: 'bg-yellow-400',
  negotiation: 'bg-orange-400',
  won: 'bg-green-500',
  lost: 'bg-red-400',
};

function StatCard({ icon, label, value, sub, iconBg = 'bg-primary/10', iconColor = 'text-primary', valueColor = 'text-foreground' }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon} size={22} className={iconColor} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <p className={`text-2xl font-bold mt-0.5 ${valueColor}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const { user, memberships: authMemberships, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('centers');

  // Data states
  const [adminCenters, setAdminCenters] = useState([]);
  const [inventoryByCenters, setInventoryByCenters] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [leadsByStage, setLeadsByStage] = useState({});
  const [leadsByCenterStage, setLeadsByCenterStage] = useState({});
  const [summaryStats, setSummaryStats] = useState(null);

  useEffect(() => {
    loadAll();
  }, [authMemberships]);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get admin's center IDs from memberships
      const adminMemberships = authMemberships?.filter(m => m?.role === 'admin') || [];
      const superAdmin = isSuperAdmin?.();

      let centerIds = [];
      let centers = [];

      if (superAdmin) {
        // Super admin sees all centers
        const { data } = await supabase?.from('centers')?.select('id, name, city, address')?.eq('is_active', true);
        centers = data || [];
        centerIds = centers?.map(c => c?.id);
      } else {
        centerIds = adminMemberships?.map(m => m?.center_id);
        if (centerIds?.length > 0) {
          const { data } = await supabase?.from('centers')?.select('id, name, city, address')?.in('id', centerIds);
          centers = data || [];
        }
      }

      setAdminCenters(centers);

      if (centerIds?.length > 0) {
        await Promise.all([
          loadInventory(centerIds),
          loadTeamMembers(centerIds),
          loadLeads(centerIds),
        ]);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadInventory = async (centerIds) => {
    try {
      const { data } = await supabase
        ?.from('inventory')
        ?.select('id, center_id, unit_name, billing_metric, capacity_seats, area_sqft, status, inventory_type')
        ?.in('center_id', centerIds);

      const byCenter = {};
      (data || [])?.forEach(unit => {
        if (!byCenter?.[unit?.center_id]) {
          byCenter[unit?.center_id] = { units: [], totalSeats: 0, bookedSeats: 0, availableSeats: 0, totalSqft: 0, bookedSqft: 0, availableSqft: 0 };
        }
        const c = byCenter?.[unit?.center_id];
        c?.units?.push(unit);
        if (unit?.billing_metric === 'seat') {
          c.totalSeats += unit?.capacity_seats || 0;
          if (unit?.status === 'booked') c.bookedSeats += unit?.capacity_seats || 0;
          else if (unit?.status === 'available') c.availableSeats += unit?.capacity_seats || 0;
        } else {
          c.totalSqft += unit?.area_sqft || 0;
          if (unit?.status === 'booked') c.bookedSqft += unit?.area_sqft || 0;
          else if (unit?.status === 'available') c.availableSqft += unit?.area_sqft || 0;
        }
      });

      setInventoryByCenters(byCenter);

      // Summary stats
      let totalSeats = 0, bookedSeats = 0, totalSqft = 0, bookedSqft = 0;
      Object.values(byCenter)?.forEach(c => {
        totalSeats += c?.totalSeats;
        bookedSeats += c?.bookedSeats;
        totalSqft += c?.totalSqft;
        bookedSqft += c?.bookedSqft;
      });
      setSummaryStats({ totalSeats, bookedSeats, totalSqft, bookedSqft });
    } catch (err) {
      console.error('loadInventory error:', err);
    }
  };

  const loadTeamMembers = async (centerIds) => {
    try {
      const { data: mems } = await supabase
        ?.from('center_memberships')
        ?.select('id, user_id, center_id, role, centers(name), user_profiles(id, full_name, email, role)')
        ?.in('center_id', centerIds)
        ?.neq('role', 'admin');

      const members = mems || [];

      // Load lead activity per user
      const userIds = [...new Set(members?.map(m => m?.user_id))];
      if (userIds?.length > 0) {
        const { data: leads } = await supabase
          ?.from('leads')
          ?.select('id, assigned_user, stage, center_id')
          ?.in('center_id', centerIds)
          ?.in('assigned_user', userIds);

        const activityMap = {};
        (leads || [])?.forEach(lead => {
          if (!activityMap?.[lead?.assigned_user]) {
            activityMap[lead?.assigned_user] = { total: 0, won: 0, active: 0, lost: 0 };
          }
          activityMap[lead?.assigned_user].total++;
          if (lead?.stage === 'won') activityMap[lead?.assigned_user].won++;
          else if (lead?.stage === 'lost') activityMap[lead?.assigned_user].lost++;
          else activityMap[lead?.assigned_user].active++;
        });

        const enriched = members?.map(m => ({
          ...m,
          activity: activityMap?.[m?.user_id] || { total: 0, won: 0, active: 0, lost: 0 },
        }));
        setTeamMembers(enriched);
      } else {
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('loadTeamMembers error:', err);
    }
  };

  const loadLeads = async (centerIds) => {
    try {
      const { data } = await supabase
        ?.from('leads')
        ?.select('id, stage, center_id, probability, created_at')
        ?.in('center_id', centerIds);

      const stageMap = {};
      const byCenterStage = {};

      LEAD_STAGES?.forEach(s => { stageMap[s] = 0; });

      (data || [])?.forEach(lead => {
        stageMap[lead?.stage] = (stageMap?.[lead?.stage] || 0) + 1;
        if (!byCenterStage?.[lead?.center_id]) {
          byCenterStage[lead?.center_id] = {};
          LEAD_STAGES?.forEach(s => { byCenterStage[lead?.center_id][s] = 0; });
        }
        byCenterStage[lead?.center_id][lead?.stage] = (byCenterStage?.[lead?.center_id]?.[lead?.stage] || 0) + 1;
      });

      setLeadsByStage(stageMap);
      setLeadsByCenterStage(byCenterStage);
    } catch (err) {
      console.error('loadLeads error:', err);
    }
  };

  // ─── Derived ───────────────────────────────────────────────────────────────
  const totalLeads = Object.values(leadsByStage)?.reduce((s, v) => s + v, 0);
  const wonLeads = leadsByStage?.won || 0;
  const conversionRate = totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 100) : 0;
  const activeLeads = totalLeads - (leadsByStage?.won || 0) - (leadsByStage?.lost || 0);

  const funnelStages = LEAD_STAGES?.filter(s => s !== 'lost');
  const maxFunnelCount = Math.max(...funnelStages?.map(s => leadsByStage?.[s] || 0), 1);

  const tabs = [
    { id: 'centers', label: 'Centers & Inventory', icon: 'Building' },
    { id: 'team', label: 'Team Performance', icon: 'Users' },
    { id: 'funnel', label: 'Lead Funnel', icon: 'Filter' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={40} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {adminCenters?.length} assigned center{adminCenters?.length !== 1 ? 's' : ''} · Operations overview
          </p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
        >
          <Icon name="RefreshCw" size={15} />
          Refresh
        </button>
      </div>
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="Building"
          label="Assigned Centers"
          value={adminCenters?.length}
          sub="Active centers under management"
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <StatCard
          icon="Armchair"
          label="Total Seats"
          value={summaryStats?.totalSeats?.toLocaleString() ?? '—'}
          sub={`${summaryStats?.bookedSeats?.toLocaleString() ?? 0} booked`}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
        />
        <StatCard
          icon="Target"
          label="Active Leads"
          value={activeLeads}
          sub={`${wonLeads} won · ${conversionRate}% conversion`}
          iconBg="bg-orange-50"
          iconColor="text-orange-600"
        />
        <StatCard
          icon="Users"
          label="Team Members"
          value={teamMembers?.length}
          sub="Across all assigned centers"
          iconBg="bg-teal-50"
          iconColor="text-teal-600"
        />
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* ── Tab: Centers & Inventory ── */}
      {activeTab === 'centers' && (
        <div className="space-y-4">
          <SectionHeader title="Inventory Overview per Center" subtitle="Seat and sqft breakdown by status" />
          {adminCenters?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Icon name="Building" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No centers assigned to your account.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {adminCenters?.map(center => {
                const inv = inventoryByCenters?.[center?.id];
                const totalUnits = inv?.units?.length || 0;
                const bookedUnits = inv?.units?.filter(u => u?.status === 'booked')?.length || 0;
                const availableUnits = inv?.units?.filter(u => u?.status === 'available')?.length || 0;
                const onHoldUnits = inv?.units?.filter(u => u?.status === 'on_hold')?.length || 0;
                const seatOccupancy = inv?.totalSeats > 0 ? Math.round((inv?.bookedSeats / inv?.totalSeats) * 100) : 0;
                const sqftOccupancy = inv?.totalSqft > 0 ? Math.round((inv?.bookedSqft / inv?.totalSqft) * 100) : 0;
                const centerLeads = leadsByCenterStage?.[center?.id] || {};
                const centerTotalLeads = Object.values(centerLeads)?.reduce((s, v) => s + v, 0);
                const centerWon = centerLeads?.won || 0;
                const centerConv = centerTotalLeads > 0 ? Math.round((centerWon / centerTotalLeads) * 100) : 0;

                return (
                  <div key={center?.id} className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-foreground">{center?.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{center?.city}{center?.address ? ` · ${center?.address}` : ''}</p>
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        {totalUnits} units
                      </span>
                    </div>

                    {/* Inventory Status Row */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="bg-green-50 rounded-lg p-2.5 text-center">
                        <p className="text-lg font-bold text-green-700">{availableUnits}</p>
                        <p className="text-xs text-green-600">Available</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2.5 text-center">
                        <p className="text-lg font-bold text-red-700">{bookedUnits}</p>
                        <p className="text-xs text-red-600">Booked</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-2.5 text-center">
                        <p className="text-lg font-bold text-yellow-700">{onHoldUnits}</p>
                        <p className="text-xs text-yellow-600">On Hold</p>
                      </div>
                    </div>

                    {/* Seat Occupancy */}
                    {inv?.totalSeats > 0 && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Seat Occupancy</span>
                          <span className="font-medium text-foreground">{inv?.bookedSeats}/{inv?.totalSeats} seats ({seatOccupancy}%)</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              seatOccupancy >= 80 ? 'bg-green-500' : seatOccupancy >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${seatOccupancy}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Sqft Occupancy */}
                    {inv?.totalSqft > 0 && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Sqft Occupancy</span>
                          <span className="font-medium text-foreground">
                            {inv?.bookedSqft?.toLocaleString()}/{inv?.totalSqft?.toLocaleString()} sqft ({sqftOccupancy}%)
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              sqftOccupancy >= 80 ? 'bg-green-500' : sqftOccupancy >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${sqftOccupancy}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Lead Conversion */}
                    <div className="pt-3 border-t border-border flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Lead Conversion</span>
                      <span className={`text-sm font-semibold ${
                        centerConv >= 30 ? 'text-green-600' : centerConv >= 15 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {centerConv}% ({centerWon}/{centerTotalLeads})
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* ── Tab: Team Performance ── */}
      {activeTab === 'team' && (
        <div className="space-y-4">
          <SectionHeader title="Team Performance Metrics" subtitle="Lead activity and conversion rates per team member" />
          {teamMembers?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No team members found in your centers.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Member</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Center</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Role</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Leads</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Active</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Won</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Conversion</th>
                      <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">SLA Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {teamMembers?.map((member, idx) => {
                      const act = member?.activity || { total: 0, won: 0, active: 0, lost: 0 };
                      const conv = act?.total > 0 ? Math.round((act?.won / act?.total) * 100) : 0;
                      const slaOk = act?.active <= 10; // simple SLA: flag if >10 active leads
                      const roleBadge = {
                        center_manager: 'bg-teal-100 text-teal-700',
                        sales: 'bg-orange-100 text-orange-700',
                        operations: 'bg-gray-100 text-gray-700',
                      }?.[member?.role] || 'bg-gray-100 text-gray-600';

                      return (
                        <tr key={member?.id || idx} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-semibold text-primary">
                                  {member?.user_profiles?.full_name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{member?.user_profiles?.full_name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">{member?.user_profiles?.email || ''}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{member?.centers?.name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadge}`}>
                              {member?.role?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-medium text-foreground">{act?.total}</td>
                          <td className="px-4 py-3 text-center text-sm text-blue-600 font-medium">{act?.active}</td>
                          <td className="px-4 py-3 text-center text-sm text-green-600 font-medium">{act?.won}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm font-semibold ${
                              conv >= 30 ? 'text-green-600' : conv >= 15 ? 'text-yellow-600' : 'text-red-500'
                            }`}>
                              {conv}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              slaOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {slaOk ? 'On Track' : 'Overloaded'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {/* ── Tab: Lead Funnel ── */}
      {activeTab === 'funnel' && (
        <div className="space-y-6">
          <SectionHeader title="Lead Conversion Funnel" subtitle="Pipeline stages with conversion rates" />

          {/* Summary Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{totalLeads}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Leads</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{wonLeads}</p>
              <p className="text-xs text-muted-foreground mt-1">Won</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${
                conversionRate >= 30 ? 'text-green-600' : conversionRate >= 15 ? 'text-yellow-600' : 'text-red-600'
              }`}>{conversionRate}%</p>
              <p className="text-xs text-muted-foreground mt-1">Conversion Rate</p>
            </div>
          </div>

          {/* Funnel Visualization */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-sm font-semibold text-foreground mb-5">Pipeline Funnel</h3>
            <div className="space-y-2">
              {funnelStages?.map((stage, idx) => {
                const count = leadsByStage?.[stage] || 0;
                const pct = maxFunnelCount > 0 ? Math.round((count / maxFunnelCount) * 100) : 0;
                const prevCount = idx > 0 ? (leadsByStage?.[funnelStages?.[idx - 1]] || 0) : count;
                const dropRate = prevCount > 0 && idx > 0 ? Math.round(((prevCount - count) / prevCount) * 100) : null;

                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div className="w-28 text-right">
                      <span className="text-xs font-medium text-muted-foreground">{STAGE_LABELS?.[stage]}</span>
                    </div>
                    <div className="flex-1 relative">
                      <div className="h-8 bg-muted rounded-lg overflow-hidden">
                        <div
                          className={`h-full ${STAGE_COLORS?.[stage]} rounded-lg transition-all duration-500 flex items-center justify-end pr-2`}
                          style={{ width: `${Math.max(pct, count > 0 ? 4 : 0)}%` }}
                        >
                          {count > 0 && pct > 15 && (
                            <span className="text-xs font-semibold text-white">{count}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-16 text-left">
                      <span className="text-sm font-semibold text-foreground">{count}</span>
                      {dropRate !== null && dropRate > 0 && (
                        <span className="text-xs text-red-500 ml-1">-{dropRate}%</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Lost leads note */}
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Lost leads (excluded from funnel)</span>
              <span className="text-sm font-semibold text-red-500">{leadsByStage?.lost || 0}</span>
            </div>
          </div>

          {/* Per-Center Lead Breakdown */}
          {adminCenters?.length > 1 && (
            <div className="bg-card border border-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Lead Stages by Center</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left pb-2 text-xs font-medium text-muted-foreground uppercase">Center</th>
                      {LEAD_STAGES?.filter(s => s !== 'lost')?.map(s => (
                        <th key={s} className="text-center pb-2 text-xs font-medium text-muted-foreground uppercase">{STAGE_LABELS?.[s]}</th>
                      ))}
                      <th className="text-center pb-2 text-xs font-medium text-muted-foreground uppercase">Conv%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {adminCenters?.map(center => {
                      const cl = leadsByCenterStage?.[center?.id] || {};
                      const ct = Object.values(cl)?.reduce((s, v) => s + v, 0);
                      const cw = cl?.won || 0;
                      const cc = ct > 0 ? Math.round((cw / ct) * 100) : 0;
                      return (
                        <tr key={center?.id} className="hover:bg-muted/20">
                          <td className="py-2 pr-4 font-medium text-foreground">{center?.name}</td>
                          {LEAD_STAGES?.filter(s => s !== 'lost')?.map(s => (
                            <td key={s} className="py-2 text-center text-muted-foreground">{cl?.[s] || 0}</td>
                          ))}
                          <td className="py-2 text-center">
                            <span className={`font-semibold ${
                              cc >= 30 ? 'text-green-600' : cc >= 15 ? 'text-yellow-600' : 'text-red-500'
                            }`}>{cc}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
